<script setup lang="ts">
  import { socialUrls } from '#shared/data/socialNetworks';

  definePageMeta({
    availableForGuests: true,
    hideRightBar: true,
  });

  const PVP_SLIDES = [
    {
      img: '/images/about/games/pvp-multi-mines.webp',
      id: 6,
    },
    {
      img: '/images/about/games/pvp-max-blast-champs.webp',
      id: 864,
    },
    {
      img: '/images/about/games/pvp-multi-plinko.webp',
      id: 1,
    },
  ];

  const SOLO_GAMES = [
    {
      img: '/images/about/games/fp-olympus-plinko.webp',
      title: 'Olympus Plinko',
      id: 11,
    },
  ];

  const INTRODUCING_TITLE_TEXT = 'INTRODUCING MAXDUEL';
  const MD_DESCRIPTION = `
    MaxDuel is the ultimate Player vs Player (PvP) game platform where you compete in original
    games of skill for real cash prizes. Experience PvP versions of your favorite games and play against others in a
    "winner take all" format to take the entire Prize Pot!`;
  const CHALLENGE_FRIENDS_IMG_DESKTOP = '/images/about/art-devices-desktop.webp';
  const CHALLENGE_FRIENDS_IMG_MOBILE = '/images/about/art-devices-mobile.webp';
  const ART_CHALLENGE_DEVICES_DESKTOP = '/images/about/art-challenge-devices-desktop.webp';
  const ART_CHALLENGE_DEVICES_MOBILE = '/images/about/art-challenge-devices-mobile.webp';

  const { isDesktop } = useDeviceType();

  const deviceArtUrl = ref(CHALLENGE_FRIENDS_IMG_DESKTOP);
  const challengeFriendsImgUrl = ref(ART_CHALLENGE_DEVICES_DESKTOP);

  onMounted(() => {
    if (!isDesktop.value) {
      deviceArtUrl.value = CHALLENGE_FRIENDS_IMG_MOBILE;
      challengeFriendsImgUrl.value = ART_CHALLENGE_DEVICES_MOBILE;
    }
  });

  const pvpGames = computed(() =>
    PVP_SLIDES.map(({ img, id }) => ({
      img,
      ...stores.games.getGameConfigById(id),
    })),
  );
  const soloGames = computed(() =>
    SOLO_GAMES.map(({ img, id }) => ({
      img,
      ...stores.games.getGameConfigById(id),
    })),
  );

  useSchemaOrg([
    defineWebPage({
      '@type': 'AboutPage',
      name: INTRODUCING_TITLE_TEXT,
    }),
    defineArticle({
      headline: INTRODUCING_TITLE_TEXT,
      description: MD_DESCRIPTION,
    }),
  ]);

  async function init() {
    if (!stores.games.list.length) {
      await useAsyncData('active-games', () => stores.games.getGamesList());
    }
  }

  await init();
</script>

<template>
  <div class="py-4 w-full flex flex-col gap-6 max-w-[62.5rem] mx-auto">
    <section class="px-4 md:px-10 pt-10 relative rounded-[0.625rem] flex flex-col md:flex-row about-bg-gradient">
      <div
        class="flex flex-col gap-6 w-full mx-auto max-w-[30.75rem] flex-shrink-0 text-center md:text-left md:max-w-[55vw] lg:max-w-[60%] pb-10"
      >
        <h2 class="text-white font-barlow text-[3rem] md:text-4xl lg:text-6xl font-extrabold leading-[110%]">
          {{ INTRODUCING_TITLE_TEXT }}
        </h2>
        <h3 class="text-white font-barlow text-[1.125rem] md:text-xl lg:text-2xl font-semibold leading-[120%]">
          The Ultimate Player vs Player Platform
        </h3>
        <p class="text-white font-barlow text-base font-normal leading-[150%]">
          {{ MD_DESCRIPTION }}
        </p>
      </div>
      <CoreImage
        class="w-full md:min-w-none max-h-[25rem] md:max-h-none object-cover max-w-[32.5rem] mx-auto md:w-[40.625rem] md:max-w-[35vw] aspect-[670/978] md:aspect-[1292/770] m-0 lg:-translate-x-[5vw] desktop-1600:-translate-x-[7.5vw] transform"
        :src="deviceArtUrl"
      />
    </section>

    <section
      class="overflow-hidden p-4 md:p-10 relative rounded-[0.625rem] flex flex-col md:flex-row gap-10 bg-gradient-blue bg-center bg-cover bg-no-repeat"
    >
      <div class="absolute h-full w-full z-0 left-0 top-0 about-circle-bg-left"></div>
      <CoreGameSlider class="flex-shrink-0 flex-grow-0 flex-basis-[17.5rem] mx-auto md:mx-0" :slides="pvpGames" />
      <div class="max-w-[32.5rem] w-full relative z-10">
        <h2
          class="text-white text-center md:text-left font-barlow text-[2rem] md:text-[2.75rem] not-italic font-extrabold leading-[120%] mb-6"
        >
          PLAYER vs PLAYER
        </h2>
        <p class="text-white font-barlow text-base not-italic font-normal leading-[150%]">
          MaxDuel introduces original PvP versions of popular games such as MultiPlinko, MultiMines, Roll or Hold and
          Escape the Circle.
          <br />
          <br />
          All Player vs Player games are developed in-house by the MaxDuel team, enticing players with a different, new
          experience of game action and experience.
        </p>
      </div>
    </section>

    <section
      class="p-4 md:p-10 relative rounded-[0.625rem] flex flex-col-reverse md:flex-row justify-between gap-10 bg-gradient-blue about-left-circle-bg overflow-hidden"
    >
      <div class="absolute h-full w-full z-0 left-0 top-0 about-circle-bg-right"></div>
      <div class="max-w-[32.5rem] w-full relative z-10">
        <h2
          class="text-white text-center md:text-left font-barlow text-[2rem] md:text-[2.75rem] not-italic font-extrabold leading-[120%] mb-6"
        >
          SOLOPLAY
        </h2>
        <p class="text-white font-barlow text-base not-italic font-normal leading-[150%]">
          Play quick, fast rounds in single player versions of classic games such as MaxMines.
          <br />
          <br />
          MaxDuel sets the standard for game design, offering a truly original and exclusive game experience.
        </p>
      </div>
      <CoreGameSlider class="flex-shrink-0 flex-grow-0 flex-basis-[17.5rem] mx-auto md:mx-0" :slides="soloGames" />
    </section>

    <section
      class="pr-4 pt-10 relative rounded-[0.625rem] flex flex-col desktop-1040:flex-row about-caf-section about-left-circle-bg overflow-hidden"
    >
      <div class="absolute h-full w-full z-0 left-0 top-0 about-circle-bg-left"></div>
      <div class="overflow-hidden w-full max-h-[27.5rem] absolute desktop-1040:relative z-10 flex justify-center">
        <CoreImage
          class="object-cover xs:object-contain flex-grow-1 relative w-full md:max-w-[100%] xs:max-h-[28.125rem] object-top md:object-bottom"
          :src="challengeFriendsImgUrl"
          alt="CHALLENGE FRIENDS"
        />
      </div>
      <div
        class="flex-basis-[32.5rem] px-4 flex-grow-1 mx-auto pt-[20.3125rem] flex-shrink-0 max-w-[32.5rem] w-full desktop-1040:pt-[4.5rem] pb-[3.5rem] relative z-10"
      >
        <h2
          class="text-white text-center md:text-left font-barlow text-[2rem] md:text-[2.75rem] not-italic font-extrabold leading-[120%] mb-6"
        >
          CHALLENGE FRIENDS
        </h2>
        <p class="text-white font-barlow text-[1.0625rem] font-semibold leading-[150%]">
          MaxDuel is the gold standard in transparency, exclusivity and provably fair games. With the
          <span class="text-white font-barlow text-[1.0625rem] italic font-extrabold leading-[150%]">
            CHALLENGE FRIENDS
          </span>
          feature, the player can create their own ring fenced game with
          <span class="text-white font-barlow text-[1.0625rem] font-extrabold leading-[150%]">ONLY</span>
          invited guests. This means each win is verified and confirmed as real, by any participant including the host.
          <br />
          <br />
          No more doubts about "unfair" games, you control the action with MaxDuel's
          <span class="text-white font-barlow text-[1.0625rem] font-extrabold leading-[150%]">CHALLENGE FRIENDS.</span>
        </p>
      </div>
    </section>

    <section
      class="relative overflow-hidden rounded-[0.625rem] about-discord-bg flex flex-col md:flex-row py-[3rem] px-[1rem] md:px-[2.5rem]"
    >
      <div class="flex flex-col gap-6 max-w-[26.25rem] md:max-w-[50vw] text-center mx-auto md:text-left">
        <h2 class="text-white font-barlow text-[2rem] font-extrabold leading-[120%]">JOIN THE MAXDUEL COMMUNITY</h2>
        <p class="text-white font-barlow text-[1rem] font-normal leading-[150%]">
          Join our
          <a
            :href="socialUrls.discord"
            class="text-[#0080FF] font-medium cursor-pointer underline"
            target="_blank"
            rel="noopener"
          >
            Discord
          </a>
          and follow us on social media for sneak peaks, free stuff and much more!
        </p>

        <CoreButton
          color="secondary"
          class="w-fit mx-auto md:mx-0"
          :href="socialUrls.discord"
          target="_blank"
          rel="noopener"
        >
          <div class="flex items-center gap-2">
            <SvgoDiscord class="mt-1 w-[23px] h-[18px]" />
            <span>Join Us on Discord</span>
          </div>
        </CoreButton>
      </div>

      <div class="m-0 overflow-visible basis-[50%]">
        <CoreImage
          class="relative md:absolute -bottom-20 md:bottom-0 min-[57.75rem]:-bottom-15 md:right-0 md:block mx-auto w-full max-w-[22.5rem] md:max-w-none md:w-[40%]"
          src="/images/about/discord-v2.webp"
          alt="discord image"
        />
      </div>
    </section>
  </div>
</template>
